import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Sparkles, Heart, ArrowRight, Zap, Eye, Gift } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { usePaywall } from "@/contexts/PaywallContext";
import { Button } from "@/components/ui/button";
import SwipeFeed from "@/pages/SwipeFeed";
import StatsCard from "@/components/StatsCard";
import NotificationBell from "@/components/NotificationBell";
import WhoLikedYouSheet from "@/components/WhoLikedYouSheet";
import { api } from "@/lib/api";

const VIBE_OF_THE_DAY = [
    { emoji: "🔮", text: "you're serving main-character energy today" },
    { emoji: "🌙", text: "night-owl mode: activated" },
    { emoji: "🧋", text: "boba-run buddy potential: high" },
    { emoji: "✨", text: "your vibe is immaculate" },
    { emoji: "🎧", text: "playlist-share worthy connections today" },
    { emoji: "🪩", text: "chaos and comfort — a rare combo" },
    { emoji: "🍜", text: "shared-noodle-nights energy loading" },
    { emoji: "🌿", text: "plant-parent energy in the air" },
];

export default function MatchTab() {
    const { user } = useAuth();
    const { status } = usePaywall();
    const [swiping, setSwiping] = useState(false);
    const [likesOpen, setLikesOpen] = useState(false);
    const [likesCount, setLikesCount] = useState(0);
    const [isPremium, setIsPremium] = useState(false);

    const promoActive = !!status?.free_promo_active;
    const promoUntil = status?.free_promo_until;
    const daysLeft = useMemo(() => {
        if (!promoActive || !promoUntil) return null;
        const diff = new Date(promoUntil) - new Date();
        return Math.max(0, Math.ceil(diff / 86400000));
    }, [promoActive, promoUntil]);

    useEffect(() => {
        let alive = true;
        const load = async () => {
            try {
                const { data } = await api.get("/likes/received");
                if (!alive) return;
                setLikesCount(data.count || 0);
                setIsPremium(!!data.premium);
            } catch (e) {}
        };
        load();
        const t = setInterval(load, 20000);
        return () => {
            alive = false;
            clearInterval(t);
        };
    }, []);

    const vibe = useMemo(() => {
        const seed = new Date().toISOString().slice(0, 10);
        let h = 0;
        for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
        return VIBE_OF_THE_DAY[h % VIBE_OF_THE_DAY.length];
    }, []);

    if (swiping) return <SwipeFeed me={user} onExit={() => setSwiping(false)} />;

    return (
        <div className="mx-auto max-w-xl px-5 pt-8 pb-24" data-testid="match-tab">
            <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-3 py-1.5 text-xs font-black uppercase tracking-widest">
                    <Sparkles className="h-3.5 w-3.5" strokeWidth={3} /> dashboard
                </div>
                <div className="flex items-center gap-2">
                    <motion.div
                        initial={{ scale: 0.7, rotate: -20, opacity: 0 }}
                        animate={{ scale: 1, rotate: 4, opacity: 1 }}
                        transition={{ delay: 0.2, type: "spring", stiffness: 180, damping: 12 }}
                        className="sticker rounded-2xl bg-pop-lime border-2 border-foreground px-3 py-1.5 text-[10px] font-black uppercase tracking-widest"
                        data-testid="vibe-of-day-badge"
                    >
                        {vibe.emoji} today's read
                    </motion.div>
                    <NotificationBell />
                </div>
            </div>

            <motion.h1
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-5 text-4xl sm:text-5xl font-black tracking-tighter leading-[0.95]"
            >
                hey {user?.name?.split(" ")[0]?.toLowerCase() || "you"},
                <br />
                <span className="font-fancy text-gradient-purple">meet someone</span>
            </motion.h1>

            {/* Vibe of the day card */}
            <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="mt-5 rounded-3xl border-2 border-foreground p-4 bg-pop-magenta text-white flex items-center gap-4"
                data-testid="vibe-of-day-card"
            >
                <div className="flex-shrink-0 text-4xl">{vibe.emoji}</div>
                <div className="min-w-0">
                    <div className="text-[10px] font-black uppercase tracking-widest opacity-80">vibe of the day</div>
                    <div className="text-sm font-bold leading-snug mt-0.5">{vibe.text}</div>
                </div>
            </motion.div>

            {/* Stats dashboard */}
            <StatsCard user={user} />

            {/* Free launch promo banner */}
            {promoActive && (
                <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    data-testid="free-promo-banner"
                    className="mt-3 rounded-3xl border-2 border-foreground bg-gradient-to-br from-primary to-pop-magenta text-primary-foreground p-4 relative overflow-hidden"
                >
                    <div className="blob" style={{ width: 180, height: 180, top: -60, right: -40, background: "white", opacity: 0.18 }} />
                    <div className="relative flex items-center gap-3">
                        <div className="h-11 w-11 rounded-2xl bg-white/20 grid place-items-center flex-shrink-0">
                            <Gift className="h-5 w-5" fill="currentColor" strokeWidth={0} />
                        </div>
                        <div className="min-w-0 flex-1">
                            <div className="font-display font-black text-lg leading-tight">
                                Launch week special · free for everyone 🎉
                            </div>
                            <div className="text-xs opacity-90">
                                {daysLeft != null ? `${daysLeft} day${daysLeft === 1 ? "" : "s"} left · ` : ""}unlimited chats, unlimited swipes, no paywalls. Enjoy the vibe ✨
                            </div>
                        </div>
                    </div>
                </motion.div>
            )}

            {/* Who Liked You */}
            {likesCount > 0 && (
                <motion.button
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.12 }}
                    onClick={() => setLikesOpen(true)}
                    data-testid="who-liked-open-btn"
                    className="mt-3 w-full text-left rounded-3xl border-2 border-foreground bg-card p-4 flex items-center gap-3 lift relative overflow-hidden"
                >
                    {/* Peeking avatar stack */}
                    <div className="flex-shrink-0 flex -space-x-3">
                        {[0, 1, 2].slice(0, Math.min(3, likesCount)).map((i) => (
                            <div
                                key={i}
                                className={`h-11 w-11 rounded-full border-2 border-card grid place-items-center text-white font-black text-sm ${
                                    i === 0 ? "bg-primary" : i === 1 ? "bg-pop-magenta" : "bg-pop-cyber"
                                } ${!isPremium ? "backdrop-blur-md" : ""}`}
                            >
                                <Eye className="h-4 w-4" strokeWidth={2.8} />
                            </div>
                        ))}
                    </div>
                    <div className="min-w-0 flex-1">
                        <div className="text-[10px] font-black uppercase tracking-widest text-primary">
                            {isPremium ? "revealed" : "hidden 👀"}
                        </div>
                        <div className="font-display font-black text-base leading-tight truncate">
                            {likesCount} {likesCount === 1 ? "person likes" : "people like"} your vibe
                        </div>
                        <div className="text-xs text-muted-foreground truncate">
                            {isPremium ? "Tap to see them all & like back" : "Tap to reveal your admirers"}
                        </div>
                    </div>
                    <span
                        className={`min-w-[36px] h-9 px-2 rounded-full text-xs font-black grid place-items-center flex-shrink-0 ${
                            isPremium ? "bg-accent text-accent-foreground" : "bg-primary text-primary-foreground"
                        }`}
                        data-testid="who-liked-count"
                    >
                        {likesCount}
                    </span>
                </motion.button>
            )}

            {/* Main CTA */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15 }}
                className="mt-6 relative overflow-hidden rounded-4xl border-2 border-foreground bg-card p-6"
            >
                <div className="blob bg-primary" style={{ width: 220, height: 220, top: -80, right: -60 }} />
                <div className="relative">
                    <div className="flex items-center gap-2 mb-3">
                        <span className="rounded-full bg-pop-lime border-2 border-foreground px-2.5 py-1 text-[10px] font-black uppercase tracking-widest inline-flex items-center gap-1">
                            🔥 let's gooo
                        </span>
                    </div>
                    <div className="font-display text-3xl sm:text-4xl font-black tracking-tighter leading-tight">
                        ready to find <span className="font-fancy text-gradient-purple">your person</span>?
                    </div>
                    <p className="mt-2 text-muted-foreground text-sm">Highest match% up first. No small talk.</p>

                    <Button
                        className="mt-5 h-14 rounded-2xl px-8 text-base font-black lift btn-shimmer w-full sm:w-auto"
                        onClick={() => setSwiping(true)}
                        data-testid="start-swiping-btn"
                    >
                        <Zap className="mr-2 h-5 w-5" fill="currentColor" strokeWidth={0} />
                        Start Swiping <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                </div>
            </motion.div>

            <WhoLikedYouSheet open={likesOpen} onClose={() => setLikesOpen(false)} />
        </div>
    );
}
